import { DateTime } from 'ts-luxon';

/**
 * Safely creates a DateTime from ISO string with validation
 */
export const createDateTime = (isoString: string, context?: string): DateTime => {
    const dateTime = DateTime.fromISO(isoString, { zone: 'utc' });
    if (!dateTime.isValid) {
        console.warn(`Invalid date string${context ? ` in ${context}` : ''}:`, isoString);
    }
    return dateTime;
};

/**
 * Safely creates a DateTime from date string (date only, no time) with validation
 */
export const createDate = (dateString: string, context?: string): DateTime => {
    const date = DateTime.fromISO(dateString, { zone: 'utc' });
    if (!date.isValid) {
        console.warn(`Invalid date string${context ? ` in ${context}` : ''}:`, dateString);
    }
    return date;
};

/**
 * Gets the current DateTime in a consistent way
 */
export const getCurrentDateTime = (): DateTime => {
    return DateTime.now();
};

/**
 * Formats a date string to a readable format
 */
export const formatDate = (dateString: string, format: string = 'MMM dd, yyyy'): string => {
    const date = createDateTime(dateString, 'formatDate');
    if (!date.isValid) {
        return 'Invalid Date';
    }
    return date.toFormat(format);
};

/**
 * Formats a date and time string to a readable format
 */
export const formatDateTime = (dateString: string, format: string = 'MMM dd, yyyy h:mm a'): string => {
    const date = createDateTime(dateString, 'formatDateTime');
    if (!date.isValid) {
        return 'Invalid Date';
    }
    return date.toFormat(format);
};

/**
 * Formats a date range, handling same-day events
 */
export const formatDateRange = (beginDate: string, endDate: string, separator = '-'): string => {
    const begin = createDateTime(beginDate, 'formatDateRange begin');
    const end = createDateTime(endDate, 'formatDateRange end');

    if (!begin.isValid || !end.isValid) {
        return 'Invalid Date Range';
    }

    if (begin.hasSame(end, 'day')) {
        return formatDate(beginDate);
    }

    return `${formatDate(beginDate)} ${separator} ${formatDate(endDate)}`;
};

/**
 * Calculates days until an event starts
 */
export const getDaysUntilEvent = (beginDate: string): number | null => {
    const begin = createDateTime(beginDate, 'getDaysUntilEvent');
    const now = getCurrentDateTime();

    if (!begin.isValid) {
        return 0;
    }

    const diff = Math.floor(begin.startOf('day').diff(now.startOf('day'), 'days').days);
    if (diff < 0) return null;
    return diff;
};

/**
 * Checks if a date is valid
 */
export const isValidDate = (dateString: string): boolean => {
    return createDateTime(dateString).isValid;
};

/**
 * Compares two dates safely
 */
export const compareDates = (dateA: string, dateB: string): number => {
    const dtA = createDateTime(dateA, 'compareDates A');
    const dtB = createDateTime(dateB, 'compareDates B');

    // Handle invalid dates by placing them at the end
    if (!dtA.isValid && !dtB.isValid) {
        return 0;
    }
    if (!dtA.isValid) {
        return 1;
    }
    if (!dtB.isValid) {
        return -1;
    }

    return dtA.toMillis() - dtB.toMillis();
};
